import React, { useCallback, type FC, type ReactNode } from 'react';
import { type Accept, useDropzone } from 'react-dropzone';

import { Button, ButtonSize, ButtonType } from './button';
import { UploadIcon } from './upload-icon';

interface FilePickerProps {
    fileTypes?: Accept;
    icon?: ReactNode;
    onFilesPicked?: (files: File[]) => void;
}

export const FilePicker: FC<FilePickerProps> = ({ fileTypes, onFilesPicked, icon }) => {
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const filesWithUniqueNames = acceptedFiles.map((file) => {
                const uniqueName = `${file.name.split('.')[0]}-${Date.now()}.${file.name.split('.').pop()}`;
                const newFile = new File([file], uniqueName, { type: file.type });
                return newFile;
            });
            onFilesPicked?.(acceptedFiles);
        },
        [onFilesPicked],
    );

    const { getRootProps, getInputProps, acceptedFiles, open } = useDropzone({
        onDrop,
        accept: fileTypes,
    });

    return (
        <div
            {...getRootProps({ className: 'dropzone' })}
            className="flex flex-col items-center justify-center w-full bg-gray-140 rounded-xl py-16 border-dashed border-[1px] border-gray-100"
        >
            <input className="input-zone" {...getInputProps()} />
            <div className="flex flex-col gap-1 items-center rounded-[5px]">
                {icon ? icon : <UploadIcon className="w-8 h-8" />}
                <p className="text-xs font-normal text-white mt-2">Drag and Drop here</p>
                <div className="flex flex-row gap-1 items-center w-44 text-xs text-gray-100">
                    <hr className="w-full bg-gray-100 border-gray-100" /> Or
                    <hr className="w-full bg-gray-100 border-gray-100" />
                </div>
                <Button
                    buttonType={ButtonType.SECONDARY}
                    buttonSize={ButtonSize.LARGE}
                    title="Browse files"
                    onClick={(e) => {
                        e?.stopPropagation();
                        open();
                    }}
                />
            </div>
        </div>
    );
};
